import { ref, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import type { MenuItem } from '@/types/routing-types';

/**
 * Composable for managing navigation menu collapse state
 * Generic implementation that works with any menu structure
 */
export function useCollapseState(menuItems: Ref<MenuItem[]>) {
  const route = useRoute();

  // Pure reactive state - tracks which menu IDs are expanded
  const expandedMenus = ref<Record<string, boolean>>({});

  /**
   * Check if a menu is expanded
   */
  function isExpanded(menuId: string): boolean {
    return expandedMenus.value[menuId] || false;
  }

  /**
   * Toggle collapse state (reactive)
   */
  function toggleCollapse(menuId: string) {
    expandedMenus.value[menuId] = !expandedMenus.value[menuId];
  }

  /**
   * Check if a route path matches an item (exact or child)
   */
  function pathMatchesItem(routePath: string, itemPath: string): boolean {
    return routePath === itemPath || routePath.startsWith(itemPath + '/');
  }

  /**
   * Auto-expand parent menus based on current route
   * Generically finds parent items whose children match the active route
   */
  function autoExpandFromRoute() {
    const currentPath = route.path;
    const currentQuery = route.query;

    // Reset all menus first
    Object.keys(expandedMenus.value).forEach(key => {
      expandedMenus.value[key] = false;
    });

    function itemMatchesRoute(item: MenuItem): boolean {
      if (!item.path) return false;
      if (item.query) {
        if (currentPath !== item.path) return false;
        return Object.entries(item.query).every(([k, v]) => currentQuery[k] === v);
      }
      return pathMatchesItem(currentPath, item.path);
    }

    function descendantMatches(node: MenuItem): boolean {
      if (itemMatchesRoute(node)) return true;
      return (node.children ?? []).some(descendantMatches);
    }

    function checkAndExpandItem(item: MenuItem) {
      if (!item.children || item.children.length === 0) return;

      const menuId = `submenu-${String(item.name)}`;
      const matchesParent = itemMatchesRoute(item);
      const matchesChild = item.children.some(descendantMatches);

      if (matchesParent || matchesChild) {
        expandedMenus.value[menuId] = true;
      }

      // Recurse so nested groups (e.g. Product groups under Projects) also expand
      item.children.forEach(checkAndExpandItem);
    }

    // Check all menu items
    menuItems.value.forEach(checkAndExpandItem);
  }

  // Watch route changes and auto-expand. Also watch query so navigating between
  // filter URLs that share a path (e.g. /projects?productId=A vs ?productId=B)
  // re-evaluates which group should be expanded. menuItems is also watched so the
  // expansion re-runs once dynamic children (projects/products) load asynchronously
  // after the initial mount — without this, a hard reload on /projects/{id} highlights
  // the active leaf but its parent group stays collapsed.
  watch(() => [route.path, route.query, menuItems.value], () => {
    autoExpandFromRoute();
  }, { immediate: true, deep: true });

  return {
    expandedMenus,
    isExpanded,
    toggleCollapse,
    autoExpandFromRoute
  };
}
